import { useEffect, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import useFetch from 'shared/hooks/useFetch';
import { queryString } from 'shared/util/query';
import type { Filters, ListSearchParams, ResultData } from '@/domain';
import { apiListAdImageWrapper } from '@/utils/urlPrepender';
import { SEARCH_TIMEOUT_ERROR_MESSAGE, SEARCH_TIMEOUT_MS } from './searchConstants';

type ListSearchConfig = {
    url: string;
    fetchKey: string;
};

const buildListSearchConfig = (filters: Filters, apiUrl: string): ListSearchConfig => {
    const { destination, ...listParams }: Filters = filters;
    const params: ListSearchParams = listParams;
    const queryStr = queryString(params);

    return {
        url: `${apiUrl}/list${queryStr}`,
        fetchKey: queryStr,
    };
};

export const useListSearch = (filters: Filters, isEnabled = true) => {
    const { apiExternalUrl } = useConfig();
    const [hasTimedOut, setHasTimedOut] = useState(false);

    const { url, fetchKey } = buildListSearchConfig(filters, apiExternalUrl);

    const { data: rawData, error } = useFetch<ResultData>({
        url,
        isEnabled: isEnabled && Boolean(fetchKey),
        options: {
            dedupingInterval: 0,
            revalidateOnFocus: false,
        },
    });

    useEffect(() => {
        if (!isEnabled || !fetchKey) return;

        setHasTimedOut(false);

        const timeout = setTimeout(() => {
            if (!rawData && !error) {
                setHasTimedOut(true);
            }
        }, SEARCH_TIMEOUT_MS);

        return () => clearTimeout(timeout);
    }, [fetchKey, isEnabled, rawData, error]);

    const data = rawData ? apiListAdImageWrapper(rawData) : undefined;
    const timeoutError = hasTimedOut && !rawData && !error ? new Error(SEARCH_TIMEOUT_ERROR_MESSAGE) : undefined;
    const loading = !rawData && !error && !hasTimedOut;

    return { data, error: error || timeoutError, loading };
};
